import { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SettingsIcon from "@mui/icons-material/Settings";
import PeopleIcon from "@mui/icons-material/People";

// MODIFIED: Updated local relative imports
import { ApproverSetupTab } from "./ApproverSetupTab";
import { ApproverListTab } from "./ApproverListTab";

export function FinancialApproverModal({
  isOpen,
  onClose,
  mode,
  selectedData,
  onSave,
  loading,
  showGeneralMessage
}) {
  const isViewOnly = mode === "view";

  const [activeTab, setActiveTab] = useState("setup");
  const [approverHierarchyCount, setApproverHierarchyCount] = useState(1);

  const [hierarchyLevels, setHierarchyLevels] = useState([
    [{ approverId: "", fullName: "" }],
  ]);

  const [errorState, setErrorState] = useState({
    approverHierarchyCount: false,
    levels: [],
    duplicateApprover: false,
    selfApprover: false,
  });

  const validateUniqueness = () => {
    const allApproverIds = [];
    let hasSelfApprover = false;
    let hasDuplicate = false;
    let duplicateApproverName = "";

    hierarchyLevels.forEach((level) => {
      level.forEach((slot) => {
        if (slot.approverId && slot.approverId !== "") {
          const approverIdNum = parseInt(slot.approverId);
          if (selectedData && parseInt(selectedData.id) === approverIdNum) {
            hasSelfApprover = true;
          }
          if (allApproverIds.includes(approverIdNum)) {
            hasDuplicate = true;
            duplicateApproverName = slot.fullName;
          } else {
            allApproverIds.push(approverIdNum);
          }
        }
      });
    });

    return { hasSelfApprover, hasDuplicate, duplicateApproverName };
  };

  const validate = () => {
    const newErrors = {
      approverHierarchyCount: false,
      levels: [],
      duplicateApprover: false,
      selfApprover: false
    };
    let valid = true;

    if (!approverHierarchyCount || approverHierarchyCount < 1) {
      newErrors.approverHierarchyCount = true;
      valid = false;
    }

    const levelErrors = hierarchyLevels.map((level) => {
      const hasAtLeastOne = level.some((slot) => slot.approverId !== "");
      if (!hasAtLeastOne) {
        valid = false;
        return true;
      }
      return false;
    });
    newErrors.levels = levelErrors;

    const { hasSelfApprover, hasDuplicate, duplicateApproverName } = validateUniqueness();

    if (hasSelfApprover) {
      newErrors.selfApprover = true;
      valid = false;
    }

    if (hasDuplicate) {
      newErrors.duplicateApprover = true;
      newErrors.duplicateApproverName = duplicateApproverName;
      valid = false;
    }

    setErrorState(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      if (errorState.levels.some(Boolean) || errorState.duplicateApprover || errorState.selfApprover) {
        setActiveTab("approvers");
      }
      return;
    }

    // MODIFIED: Note that formatting to payload logic is now also handled in the Hook
    // but we pass the raw structure to the hook for processing.
    const result = await onSave({
      selectedEmployee: selectedData,
      approverHierarchyCount,
      hierarchyLevels: hierarchyLevels // Hook will handle the mapping to .approverId
    });

    if (result?.success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setActiveTab("setup");
    setApproverHierarchyCount(1);
    setHierarchyLevels([[{ approverId: "", fullName: "" }]]);
    setErrorState({
      approverHierarchyCount: false,
      levels: [],
      duplicateApprover: false,
      selfApprover: false
    });
    onClose();
  };

  useEffect(() => {
    if (!isOpen || !selectedData) return;

    const existingCount = selectedData.approverHierarchyCount ?? 1;
    setApproverHierarchyCount(existingCount);

    const existingDetails = selectedData.mainApproverDetails ?? [];

    if (existingDetails.length > 0) {
      const grouped = [];
      for (let i = 1; i <= existingCount; i++) {
        const levelApprovers = existingDetails
          .filter((d) => d.hierarchy === i)
          .map((d) => ({
            approverId: d.approverId,
            fullName: d.approverFullName ?? "",
          }));

        grouped.push(
          levelApprovers.length > 0
            ? levelApprovers
            : [{ approverId: "", fullName: "" }]
        );
      }
      setHierarchyLevels(grouped);
    } else {
      setHierarchyLevels(
        Array.from({ length: existingCount }, () => [
          { approverId: "", fullName: "" },
        ])
      );
    }

    setErrorState({
      approverHierarchyCount: false,
      levels: [],
      duplicateApprover: false,
      selfApprover: false
    });
    setActiveTab("setup");
  }, [isOpen, selectedData]);

  useEffect(() => {
    setHierarchyLevels((prev) => {
      const current = [...prev];
      if (approverHierarchyCount > current.length) {
        const toAdd = approverHierarchyCount - current.length;
        for (let i = 0; i < toAdd; i++) {
          current.push([{ approverId: "", fullName: "" }]);
        }
      } else if (approverHierarchyCount < current.length) {
        current.splice(approverHierarchyCount);
      }
      return current;
    });
  }, [approverHierarchyCount]);

  if (!isOpen) return null;

  const tabClass = (tabName) =>
    `px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 cursor-pointer border-none bg-transparent ${activeTab === tabName
      ? "border-blue-500 text-blue-600 bg-blue-50"
      : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
    }`;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700">
      <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[70%] lg:w-[60%] xl:w-[50%]">

        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-3 shadow-sm">
          <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
            <AccountBalanceIcon className="text-blue-500" fontSize="small" />
            <span className="text-lg text-gray-600">Financial Approver Hierarchy</span>
          </h2>
          <button onClick={handleClose} className="border-none bg-transparent p-1 text-gray-400 hover:text-gray-700 cursor-pointer">
            <CloseIcon fontSize="medium" />
          </button>
        </div>

        <div className="border-b border-gray-200 bg-white pt-3">
          <div className="flex space-x-1 px-6">
            <button onClick={() => setActiveTab("setup")} className={tabClass("setup")}>
              <SettingsIcon fontSize="small" className="mr-1" /> Setup
            </button>
            <button onClick={() => setActiveTab("approvers")} className={tabClass("approvers")}>
              <PeopleIcon fontSize="small" className="mr-1" /> Approvers
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === "setup" && (
            <ApproverSetupTab
              isViewOnly={isViewOnly}
              selectedEmployee={selectedData}
              approverHierarchyCount={approverHierarchyCount}
              setApproverHierarchyCount={setApproverHierarchyCount}
              errorState={errorState}
            />
          )}

          {activeTab === "approvers" && (
            <ApproverListTab
              isViewOnly={isViewOnly}
              approverHierarchyCount={approverHierarchyCount}
              hierarchyLevels={hierarchyLevels}
              setHierarchyLevels={setHierarchyLevels}
              errorState={errorState}
              selectedEmployeeId={selectedData?.id}
              showGeneralMessage={showGeneralMessage}
            />
          )}
        </div>

        <div className="flex items-center justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white px-5 py-4 shadow-sm">
          <button onClick={handleClose} className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-sm text-white hover:bg-gray-500">
            Cancel
          </button>
          {!isViewOnly && (
            <button onClick={handleSubmit} disabled={loading} className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-sm text-white hover:bg-blue-600 disabled:opacity-60">
              {loading ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}